import { useState } from 'react';
import toast from 'react-hot-toast';
import { reportIssueApi } from '../lib/api';
import { useAuthStore } from '../store/useAuthStore';

/**
 * In-app issue reporter. Visual sibling of ContactModal (same header/layout/
 * button styling) so the two feel like one product surface, but tailored to
 * authenticated users:
 *
 *   • Identity (name / email / org / app version) is shown as read-only context
 *     instead of asking the user to retype it. The server pulls these from the
 *     session for the outbound email, so the chips here are just transparency
 *     about what we're attaching to the report.
 *   • Subject is optional but encouraged — it becomes the email headline.
 *   • Screenshot picker — opt-in. Accepts a single image up to 5MB; the file
 *     is read as base64 and posted with the rest of the payload so it lands
 *     as a real attachment in the team inbox.
 *
 * Replaces the old `mailto:` launcher, which only worked for users with a
 * configured mail client and couldn't deliver an attachment at all.
 */

// Resend's per-email attachment cap is 40MB, but anything over ~5MB is
// almost certainly a screen recording the user should host elsewhere — and
// it bloats our outbound payload + the team inbox. Mirror the server cap so
// the UX rejects oversized files BEFORE the user hits Send.
const MAX_FILE_BYTES = 5 * 1024 * 1024;

interface AttachmentState { filename: string; mimeType: string; contentBase64: string; sizeBytes: number }

export default function ReportIssueModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const user = useAuthStore(s => s.user);
  const org = useAuthStore(s => s.org);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState<AttachmentState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const close = () => {
    if (submitting) return;
    setSubject(''); setMessage(''); setAttachment(null); setSent(false);
    onClose();
  };

  const handleFile = (file: File | null) => {
    if (!file) { setAttachment(null); return; }
    if (!file.type.startsWith('image/')) {
      toast.error('Screenshot must be an image (PNG/JPG/etc.)');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast.error('Screenshot is too large (5MB max). Host it elsewhere and paste a link in the message.');
      return;
    }
    // FileReader.readAsDataURL gives us "data:image/png;base64,XXXX" — split
    // off the prefix so we send just the base64 payload to the server.
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const base64 = result.includes(',') ? result.split(',', 2)[1] : result;
      setAttachment({
        filename: file.name || 'screenshot.png',
        mimeType: file.type || 'image/png',
        contentBase64: base64,
        sizeBytes: file.size,
      });
    };
    reader.onerror = () => toast.error('Could not read that file. Try a different image.');
    reader.readAsDataURL(file);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error('Please describe the issue before sending.');
      return;
    }
    setSubmitting(true);
    try {
      await reportIssueApi.submit({
        subject: subject.trim() || undefined,
        message: message.trim(),
        pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        screenshot: attachment
          ? { filename: attachment.filename, mimeType: attachment.mimeType, contentBase64: attachment.contentBase64 }
          : undefined,
      });
      setSent(true);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e.response?.data?.error || 'Could not send. Try emailing getavium@gmail.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const sizeKb = attachment ? Math.max(1, Math.round(attachment.sizeBytes / 1024)) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={close} />
      <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header — mirrors ContactModal for visual continuity */}
        <div className="brand-header-gradient text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold">Report an issue</h2>
          <button
            onClick={close}
            aria-label="Close"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-card/15 text-2xl leading-none"
          >×</button>
        </div>

        {sent ? (
          <div className="px-6 py-8 text-center">
            <div className="text-3xl mb-2">✉️</div>
            <h3 className="text-base font-bold text-ink mb-2">Report sent</h3>
            <p className="text-sm text-ink-muted mb-5">
              Thanks — the team has it and will reply to {user?.email || 'your email'}{attachment ? ', with your screenshot attached' : ''}.
            </p>
            <button
              onClick={close}
              className="px-5 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg"
            >Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-3">
            <p className="text-xs text-ink-faint mb-1">
              Tell us what went wrong. We attach your account details automatically so a real person can follow up directly.
            </p>

            {/* Read-only identity context — what the server will send with the report. */}
            <div className="rounded-md bg-wash border border-line px-3 py-2 text-xs text-ink-muted space-y-0.5">
              <div><span className="text-ink-faint">From:</span> <strong className="text-ink-muted">{user?.name || 'Unknown'}</strong> · {user?.email || 'unknown'}</div>
              <div><span className="text-ink-faint">Org:</span> {org?.name || 'Unknown'}</div>
            </div>

            <label className="block">
              <span className="block text-xs font-semibold text-ink-muted mb-1">Subject <span className="font-normal text-ink-faint">(optional)</span></span>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                disabled={submitting}
                placeholder="Short summary — e.g. 'Sprint matrix not loading'"
                className="w-full px-3 py-2 text-sm border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F766E]"
                maxLength={200}
              />
            </label>

            <label className="block">
              <span className="block text-xs font-semibold text-ink-muted mb-1">What happened? *</span>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                disabled={submitting}
                rows={5}
                placeholder="What you were doing, what you expected, and what actually happened."
                className="w-full px-3 py-2 text-sm border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-[#0F766E] resize-none"
                required maxLength={5000}
              />
            </label>

            <div>
              <span className="block text-xs font-semibold text-ink-muted mb-1">Screenshot <span className="font-normal text-ink-faint">(optional, up to 5MB)</span></span>
              {attachment ? (
                <div className="flex items-center justify-between rounded-md border border-line bg-wash px-3 py-2 text-sm">
                  <div className="min-w-0 truncate">
                    <span className="mr-2">📎</span>
                    <span className="text-ink-muted font-medium">{attachment.filename}</span>
                    <span className="text-ink-faint text-xs ml-2">{sizeKb} KB</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAttachment(null)}
                    disabled={submitting}
                    className="text-xs font-medium text-ink-faint hover:text-red-600"
                  >Remove</button>
                </div>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer rounded-md border border-dashed border-line hover:border-[#0F766E] hover:bg-wash px-3 py-3 text-xs text-ink-faint">
                  <span>📎</span>
                  <span>Attach a screenshot (PNG / JPG)</span>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={submitting}
                    onChange={e => handleFile(e.target.files?.[0] ?? null)}
                    className="sr-only"
                  />
                </label>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={close}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-ink-muted hover:bg-wash rounded-md"
              >Cancel</button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark disabled:opacity-50 rounded-md"
              >{submitting ? 'Sending…' : 'Send report'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
